import React, { useState, useEffect, useContext, useRef } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { ChevronLeftIcon, ChevronRightIcon, DuplicateIcon, XIcon } from '@heroicons/react/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import WarningPanel from '../../../../components/informantManagement/WarningPanel';
import ButtonSpinner from '../../../../components/informantManagement/ButtonSpinner';
import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from '../../../../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { PatientSelfDataContext } from '../../../../context/PatientSelfDataContext';
import { getPatientFacingTerminology } from '../../../../utils/terminology';
import { sanitizeNameInput, sanitizeEmailInput } from '../../../../utils/inputSanitization';
import { validateEmail as sharedValidateEmail, validateName } from '../../../../utils/informantValidation';
import { resizeTextarea } from '../../../../utils/textareaResize';

function InformantSetupForm({ isOpen, onClose, patientDataModel }) {
  const { userData, refreshUserData } = useContext(GlobalStateContext);
  const { refreshPatientData } = useContext(PatientSelfDataContext);
  const [currentStep, setCurrentStep] = useState(1); // 1: adulthood, 2: childhood, 3: reuse-confirmation, 4: completion
  const t = getPatientFacingTerminology(userData?.organisation);
  
  // Prevent setState-after-unmount in async handlers (backend calls,
  // Firestore reads) when the modal closes while a request is in flight.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Synchronous ref-based action lock to prevent duplicate submissions.
  // Mirrors the pattern in useInformantManagement.js (actionLockRef).
  // State-based `isSubmitting` alone has a gap: handleSubmitInformant is
  // called without await from handleNext, so a fast double-click can fire
  // twice before React re-renders the disabled button.
  const actionLockRef = useRef(false);

  // Mobile keyboard adjustment state
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);

  // Handlers for mobile keyboard adjustment
  const handleTextareaFocus = () => {
    setIsKeyboardFocused(true);
  };

  const handleTextareaBlur = () => {
    setIsKeyboardFocused(false);
  };
  
  // Individual informant states
  const [adulthoodInformant, setAdulthoodInformant] = useState({
      firstName: '',
      lastName: '',
      email: '',
    relationship: '',
    isSkipped: false,
    skipReason: '',
    isSubmitting: false,
    isSuccess: false,
    hasError: false,
    errorMessage: ''
  });
  
  const [childhoodInformant, setChildhoodInformant] = useState({
      firstName: '',
      lastName: '',
      email: '',
    relationship: '',
    isSkipped: false,
    skipReason: '',
    isSubmitting: false,
    isSuccess: false,
    hasError: false,
    errorMessage: ''
  });
  
  const [submitError, setSubmitError] = useState('');
  
  // Reuse confirmation step state
  const [isReassigning, setIsReassigning] = useState(false);
  const [reassignError, setReassignError] = useState('');
  const [reassignmentSuccess, setReassignmentSuccess] = useState(false);
  
  // Form step state for skip reasons (separate for each life stage)
  const [showAdulthoodSkipForm, setShowAdulthoodSkipForm] = useState(false);
  const [showChildhoodSkipForm, setShowChildhoodSkipForm] = useState(false);

  // Reset form state when opened, then fetch fresh patient data so the
  // initialization useEffect (which depends on patientDataModel) sees the
  // current informant assignments. This prevents the "abandoned wizard"
  // scenario where a patient created an informant, closed mid-flow, and
  // reopened to stale state showing a blank form for a completed step.
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setSubmitError('');
      setShowAdulthoodSkipForm(false);
      setShowChildhoodSkipForm(false);
      setIsKeyboardFocused(false);
      setAdulthoodInformant({
          firstName: '',
          lastName: '',
          email: '',
        relationship: '',
        isSkipped: false,
        skipReason: '',
        isSubmitting: false,
        isSuccess: false,
        hasError: false,
        errorMessage: ''
      });
      setChildhoodInformant({
          firstName: '',
          lastName: '',
          email: '',
        relationship: '',
        isSkipped: false,
        skipReason: '',
        isSubmitting: false,
        isSuccess: false,
        hasError: false,
        errorMessage: ''
      });

      if (refreshPatientData) {
        refreshPatientData();
      }
    }
  }, [isOpen]);

  // Fetch user data if it's not available
  useEffect(() => {
    if (isOpen && !userData && refreshUserData) {
      refreshUserData();
    }
  }, [isOpen, userData, refreshUserData]);

  // Initialize informant data from patient context when modal opens.
  // Also auto-advances past steps that are already complete so a returning
  // patient (who abandoned the wizard mid-flow) lands on the next actionable step.
  useEffect(() => {
    const initializeInformantData = async () => {
      if (isOpen && patientDataModel) {
        const informants = patientDataModel.informants || {};
        const skipReasons = patientDataModel.informantSkipReasons || {};
        
        // Track which life stages already have an informant assigned
        let hasAdulthoodInformant = false;
        let hasChildhoodInformant = false;

        for (const informantId of Object.keys(informants)) {
          const informant = informants[informantId];
          const lifeStages = informant.assignedForLifeStages || [];
          
          let informantEmail = informant.informantEmail || '';
          
          // For backward compatibility: if email is not in patient's informants object,
          // fetch it from the informant's document in the patients collection
          if (!informantEmail) {
            try {
              console.log(`Fetching email for informant ${informantId} from their document`);
              const informantDocRef = doc(db, 'patients', informantId);
              const informantDoc = await getDoc(informantDocRef);
              
              if (informantDoc.exists()) {
                const informantData = informantDoc.data();
                informantEmail = informantData.email || '';
                console.log(`Found email for informant ${informantId}: ${informantEmail}`);
              } else {
                console.warn(`Informant document not found for ID: ${informantId}`);
              }
            } catch (error) {
              console.error(`Error fetching informant email for ${informantId}:`, error);
            }
          }
          
          if (!mountedRef.current) return;

          if (lifeStages.includes('Adulthood')) {
            hasAdulthoodInformant = true;
            setAdulthoodInformant(prev => ({
              ...prev,
              isCompleted: true,
              isSuccess: true,
              firstName: informant.informantName?.split(' ')[0] || '',
              lastName: informant.informantName?.split(' ').slice(1).join(' ') || '',
              email: informantEmail,
              relationship: informant.informantRelationshipToPatient || ''
            }));
          }

          if (lifeStages.includes('Childhood')) {
            hasChildhoodInformant = true;
            setChildhoodInformant(prev => ({
              ...prev,
              isCompleted: true,
              isSuccess: true,
              firstName: informant.informantName?.split(' ')[0] || '',
              lastName: informant.informantName?.split(' ').slice(1).join(' ') || '',
              email: informantEmail,
              relationship: informant.informantRelationshipToPatient || ''
            }));
          }
        }

        if (!mountedRef.current) return;

        if (skipReasons.adulthood) {
          hasAdulthoodInformant = true;
          setAdulthoodInformant(prev => ({
            ...prev,
            isCompleted: true,
            isSkipped: true,
            skipReason: skipReasons.adulthood
          }));
        }

        if (skipReasons.childhood) {
          hasChildhoodInformant = true;
          setChildhoodInformant(prev => ({
            ...prev,
            isCompleted: true,
            isSkipped: true,
            skipReason: skipReasons.childhood
          }));
        }

        // Auto-advance past life stages that already have an informant (or skip
        // reason) so the patient lands directly on the next incomplete step.
        if (!mountedRef.current) return;
        if (hasAdulthoodInformant && hasChildhoodInformant) {
          setCurrentStep(4);
          // Set the completion flag in Firestore if not already set. This handles
          // the case where informants were created in a prior session but the
          // wizard was abandoned before Step 4 (so the flag was never written).
          // The guard (!patientDataModel.hasCompletedInformantSetupForm) prevents:
          //   - Redundant Firestore writes on every re-render
          //   - An infinite loop: refreshPatientData → new patientDataModel ref →
          //     useEffect re-runs → updateDoc → refreshPatientData → ...
          if (!patientDataModel.hasCompletedInformantSetupForm) {
            try {
              const user = auth.currentUser;
              if (user) {
                const patientRef = doc(db, 'patients', user.uid);
                await updateDoc(patientRef, {
                  hasCompletedInformantSetupForm: true,
                  informantSetupCompletedAt: new Date()
                });
                if (refreshPatientData) refreshPatientData();
              }
            } catch (flagError) {
              console.error('Failed to set completion flag during auto-advance:', flagError);
            }
          }
        } else if (hasAdulthoodInformant) {
          setCurrentStep(2);
        }
      }
    };

    initializeInformantData();
  }, [isOpen, patientDataModel]);

  // Thin wrapper around the shared validator that returns a boolean
  // (the shared function returns an error string or null).  This keeps
  // existing call sites like `validateEmail(email)` working without
  // changing every consumer to check for null.
  const validateEmail = (email) => sharedValidateEmail(email) === null;

  // Handle form input changes
  // Applies real-time sanitization for name and email fields to prevent invalid characters
  const handleChange = (lifeStage, field, value) => {
    const setter = lifeStage === 'adulthood' ? setAdulthoodInformant : setChildhoodInformant;
    
    // Apply appropriate sanitization based on field type.  Relationship
    // is treated as a name-like field (same character restrictions).
    let sanitizedValue = value;
    if (field === 'firstName' || field === 'lastName' || field === 'relationship') {
      sanitizedValue = sanitizeNameInput(value);
    } else if (field === 'email') {
      sanitizedValue = sanitizeEmailInput(value);
    }
    
    setter(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  // Map Firebase / Cloud Function errors to user-friendly messages.
  // Mirrors the ERROR_MESSAGES map in useInformantManagement.js for
  // consistent messaging across all informant flows.
  const getErrorMessage = (error) => {
    const code = error?.code?.replace('functions/', '') || '';
    const msg = error?.message || '';

    if (code === 'already-exists' || msg.includes('already exists') || msg.includes('EmailAlreadyExistsError')) {
      return 'This email is already in use. Please use a different email address.';
    }
    if (code === 'invalid-argument') {
      return msg || 'Some of the details provided are invalid. Please check and try again.';
    }
    if (code === 'permission-denied' || code === 'unauthenticated') {
      return 'You don\'t have permission to perform this action. Please sign out and back in.';
    }
    if (code === 'not-found') {
      return 'The requested record was not found. Please refresh and try again.';
    }
    if (code === 'unavailable' || code === 'deadline-exceeded') {
      return 'The service is temporarily unavailable. Please try again in a moment.';
    }
    if (code === 'failed-precondition' || msg.includes('already assigned')) {
      // Self-heal: refresh data so the initialization useEffect re-populates
      // form state with the existing informant and auto-advances past this step.
      if (refreshPatientData) refreshPatientData();
      return `A ${t.lowerSingular} has already been set up for this life stage. The page will update momentarily.`;
    }
    return msg || `An error occurred while setting up the ${t.lowerSingular}. Please check the details and try again.`;
  };

  // Navigate to reuse confirmation step
  const handleReuseAdulthoodInformant = () => {
      setCurrentStep(3);
    setShowChildhoodSkipForm(false);
  };

  // Handle reassigning informant questionnaires
  const handleReassignInformant = async () => {
    const user = auth.currentUser;
    if (!user) {
      setReassignError('User not authenticated');
      return;
    }

    setIsReassigning(true);
    setReassignError('');

    try {
      const reassignInformantQuestionnaires = httpsCallable(functions, 'reassignInformantQuestionnaires');

      const informants = patientDataModel.informants || {};
      let adulthoodInformantId = null;

      for (const [informantId, informant] of Object.entries(informants)) {
        if (informant.assignedForLifeStages?.includes('Adulthood')) {
          adulthoodInformantId = informantId;
          break;
        }
      }

      if (!adulthoodInformantId) {
        throw new Error(`Could not find Adulthood ${t.lowerSingular} to reassign`);
      }

      const result = await reassignInformantQuestionnaires({
        informantId: adulthoodInformantId,
        patientId: user.uid,
        operation: 'add_life_stage',
        lifeStage: 'Childhood'
      });

      if (!mountedRef.current) return;

      console.log('Reassignment successful:', result);

      const updatedChildhoodInformant = {
        ...childhoodInformant,
        firstName: adulthoodInformant.firstName,
        lastName: adulthoodInformant.lastName,
        email: adulthoodInformant.email,
        relationship: adulthoodInformant.relationship,
        isCompleted: true,
        isSuccess: true,
        isSkipped: false,
        skipReason: ''
      };
      setChildhoodInformant(updatedChildhoodInformant);
      setReassignmentSuccess(true);

      handleFinalCompletion(adulthoodInformant, updatedChildhoodInformant);

      if (refreshPatientData) {
        refreshPatientData();
      }

    } catch (error) {
      console.error('Error reassigning informant:', error);
      if (mountedRef.current) setReassignError(getErrorMessage(error));
    } finally {
      if (mountedRef.current) setIsReassigning(false);
    }
  };

  // Handle declining to reuse adulthood informant
  const handleDeclineReuse = () => {
    setCurrentStep(2);
    setShowChildhoodSkipForm(true);
  };

  // Validate current step using the shared validators for consistency
  // with the informant management forms.  `validateName` checks non-empty;
  // `validateEmail` checks format + length.
  const isInformantValid = (informant) => {
    return (
      !validateName(informant.firstName, 'First name') &&
      !validateName(informant.lastName, 'Last name') &&
      validateEmail(informant.email) &&
      (informant.relationship || '').trim() !== ''
    );
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1: // Adulthood informant
        if (adulthoodInformant.isSuccess) return true;
        if (showAdulthoodSkipForm) return adulthoodInformant.skipReason.trim() !== '';
        return isInformantValid(adulthoodInformant);
      case 2: // Childhood informant
        if (childhoodInformant.isSuccess) return true;
        if (showChildhoodSkipForm) return childhoodInformant.skipReason.trim() !== '';
        return isInformantValid(childhoodInformant);
      case 3: return true;
      case 4: return true;
      default: return false;
    }
  };

  // Warn the user if they're about to submit a childhood informant with
  // the same email as the adulthood informant (the backend would reject
  // it, but a proactive client-side message is friendlier).
  const hasSameEmailWarning = () => {
    if (currentStep !== 2 || showChildhoodSkipForm) return false;
    if (!childhoodInformant.email || !adulthoodInformant.email) return false;
    if (adulthoodInformant.isSkipped) return false;
    return (
      childhoodInformant.email.trim().toLowerCase() ===
      adulthoodInformant.email.trim().toLowerCase()
    );
  };

  // Handle final completion (set flags and send notifications)
  const handleFinalCompletion = async (adulthood = null, childhood = null) => {
    try {
      // Use provided states or fall back to current state
      const finalAdulthoodInformant = adulthood || adulthoodInformant;
      const finalChildhoodInformant = childhood || childhoodInformant;
      
      console.log('=== FINAL COMPLETION DEBUG ===');
      console.log('Final adulthood informant:', finalAdulthoodInformant);
      console.log('Final childhood informant:', finalChildhoodInformant);
      
      // Build notification payload
        const notificationData = {
          informants: {},
          skipReasons: {}
        };
        
      if (!finalAdulthoodInformant.isSkipped) {
          notificationData.informants.adulthood = {
          firstName: finalAdulthoodInformant.firstName?.trim(),
          lastName: finalAdulthoodInformant.lastName?.trim(),
          email: finalAdulthoodInformant.email,
          relationship: finalAdulthoodInformant.relationship?.trim()
        };
      } else if (finalAdulthoodInformant.skipReason) {
        notificationData.skipReasons.adulthood = finalAdulthoodInformant.skipReason;
      }

      if (!finalChildhoodInformant.isSkipped) {
          notificationData.informants.childhood = {
          firstName: finalChildhoodInformant.firstName?.trim(),
          lastName: finalChildhoodInformant.lastName?.trim(),
          email: finalChildhoodInformant.email,
          relationship: finalChildhoodInformant.relationship?.trim()
        };
      } else if (finalChildhoodInformant.skipReason) {
        notificationData.skipReasons.childhood = finalChildhoodInformant.skipReason;
      }

      console.log('Built notification data:', notificationData);
      console.log('============================');

      // Set completion flag in Firestore
          const user = auth.currentUser;
          if (user) {
            const patientRef = doc(db, 'patients', user.uid);
        const updatePayload = {
          hasCompletedInformantSetupForm: true,
          informantSetupCompletedAt: new Date()
        };
        if (Object.keys(notificationData.skipReasons).length > 0) {
          updatePayload.informantSkipReasons = notificationData.skipReasons;
        }
        await updateDoc(patientRef, updatePayload);
        console.log('hasCompletedInformantSetupForm flag set successfully');
      }

      // Send provider email notification (non-blocking)
      try {
        const sendInformantSetupEmail = httpsCallable(functions, 'sendInformantSetupEmail');
        await sendInformantSetupEmail(notificationData);
        console.log('Informant setup notification sent');
      } catch (emailError) {
        console.error('Email notification failed, but setup is complete:', emailError);
      }

      // Refresh patient data
            if (refreshPatientData) {
              await refreshPatientData();
        }
      } catch (error) {
      console.error('Failed to complete informant setup:', error);
      if (mountedRef.current) {
        setSubmitError('Setup completed but there was an issue updating your status. Please refresh the page.');
      }
    }
  };

  // Handle individual informant submission.
  // Uses actionLockRef to prevent duplicate submissions from rapid clicks
  // (mirrors useInformantManagement.js pattern).
  const handleSubmitInformant = async (lifeStage) => {
    if (actionLockRef.current) return;
    actionLockRef.current = true;

    const informant = lifeStage === 'adulthood' ? adulthoodInformant : childhoodInformant;
    const setter = lifeStage === 'adulthood' ? setAdulthoodInformant : setChildhoodInformant;

    if (!userData) {
      setSubmitError('User data is unavailable. Please try again later.');
      actionLockRef.current = false;
      return;
    }

    setter(prev => ({ ...prev, isSubmitting: true, hasError: false, errorMessage: '' }));
    setSubmitError('');

    try {
      const setupInformantFunction = httpsCallable(functions, 'setupNewInformant');

      const informantData = {
        informantFirstName: informant.firstName?.trim(),
        informantLastName: informant.lastName?.trim(),
        informantEmail: informant.email?.trim(),
        informantRelationshipToPatient: informant.relationship?.trim(),
        informantForLifeStage: [lifeStage === 'adulthood' ? 'Adulthood' : 'Childhood'],
        organisation: userData.organisation || '',
        isPartOfInitialSetup: true,
      };

      const result = await setupInformantFunction(informantData);

      if (!mountedRef.current) return;
      console.log(`${lifeStage} informant setup successful:`, result.data);

      setter({ ...informant, isSubmitting: false, isSuccess: true });

      // Keep context in sync so a close+reopen sees the latest state.
      // Fire-and-forget: doesn't block progression to the next step.
      if (refreshPatientData) refreshPatientData();

    } catch (error) {
      console.error(`Error setting up ${lifeStage} informant:`, error);
      if (!mountedRef.current) return;
      const errorMessage = getErrorMessage(error);
      setter(prev => ({
        ...prev,
        isSubmitting: false,
        hasError: true,
        errorMessage,
      }));
    } finally {
      actionLockRef.current = false;
    }
  };

  const handleNext = async () => {
    switch (currentStep) {
      case 1: // Adulthood informant
        if (adulthoodInformant.isSuccess) {
          // If already successful, just proceed to step 2
          setCurrentStep(2);
        } else if (showAdulthoodSkipForm) {
          // Handle skip submission - clear form fields and mark as skipped
          setAdulthoodInformant(prev => ({ 
            ...prev, 
            isSkipped: true,
            firstName: '',
            lastName: '', 
            email: '',
            relationship: ''
          }));
          setCurrentStep(2);
        } else {
          // Submit adulthood informant
          handleSubmitInformant('adulthood');
        }
        break;
      case 2: // Childhood informant
        if (childhoodInformant.isSuccess) {
          // If already successful, navigate to completion immediately and handle background operations async
          setCurrentStep(4);
          handleFinalCompletion(adulthoodInformant, childhoodInformant);
        } else if (showChildhoodSkipForm) {
          // Handle skip submission - clear form fields and mark as skipped
          const updatedChildhoodInformant = { 
            ...childhoodInformant, 
            isSkipped: true,
            firstName: '',
            lastName: '', 
            email: '',
            relationship: ''
          };
          setChildhoodInformant(updatedChildhoodInformant);
          // Navigate to completion immediately, handle background operations async
          setCurrentStep(4);
          handleFinalCompletion(adulthoodInformant, updatedChildhoodInformant);
        } else {
          // Submit childhood informant
          handleSubmitInformant('childhood');
        }
        break;
      case 3: // Reuse confirmation step
        if (reassignmentSuccess) {
          // User confirmed the success state, proceed to completion
          setCurrentStep(4);
        } else {
          // Trigger the reassignment when user clicks "Proceed"
          handleReassignInformant();
        }
        break;
      case 4: // Completion -> close
        onClose();
        break;
      default:
        break;
    }
    
    setSubmitError('');
  };

  const handleSkip = () => {
    if (currentStep === 1) {
      setShowAdulthoodSkipForm(true);
    } else if (currentStep === 2) {
      setShowChildhoodSkipForm(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Check if we're currently on a skip form for the current step
      const onCurrentStepSkipForm = (currentStep === 1 && showAdulthoodSkipForm) || 
                                   (currentStep === 2 && showChildhoodSkipForm);
      
      if (onCurrentStepSkipForm) {
        // Go back from skip form to main form
        if (currentStep === 1) {
          setShowAdulthoodSkipForm(false);
          setAdulthoodInformant(prev => ({ ...prev, skipReason: '' }));
        } else if (currentStep === 2) {
          setShowChildhoodSkipForm(false);
          setChildhoodInformant(prev => ({ ...prev, skipReason: '' }));
        }
      } else {
        // Normal back navigation
        if (currentStep === 3) {
          // Going back from reuse confirmation to childhood skip options
          handleDeclineReuse();
        } else if (currentStep === 2) {
          // Going back from childhood to adulthood - need to show appropriate adulthood state
          setCurrentStep(1);
          if (adulthoodInformant.isSkipped) {
            // Show the skip form if adulthood was skipped
            setShowAdulthoodSkipForm(true);
          }
          // If adulthood was successful, Step 1 will automatically show success state
        } else {
          setCurrentStep(prev => prev - 1);
        }
      }
      setSubmitError('');
    }
  };

  
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Show success state if adulthood informant was already successfully created
        if (adulthoodInformant.isSuccess) {
        return (
          <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4" role="status">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Successfully Added</h4>
                    <div className="space-y-1 text-sm text-green-800">
                      <p><span className="font-medium">Name:</span> {adulthoodInformant.firstName} {adulthoodInformant.lastName}</p>
                      <p className="break-all"><span className="font-medium">Email:</span> {adulthoodInformant.email}</p>
                      <p><span className="font-medium">Relationship:</span> {adulthoodInformant.relationship}</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Your adulthood {t.lowerSingular} will receive an invitation to complete their questionnaire.
              </p>
              <p className="text-sm text-gray-600">
                You'll be able to make changes later if needed.
              </p>
            </div>
          );
        }
        
        if (showAdulthoodSkipForm) {
          return (
            <div className="space-y-4">
              {/* Header with clear messaging */}
              {/* <div className="text-left">
                <h4 className="text-sm text-gray-600 mb-2">
                  If you're unable to provide an Adulthood informant, choose one option below:
                </h4>
              </div> */}
              
              {/* Single option card design */}
              
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                    <XIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <h5 className="text-sm font-semibold text-gray-900 mb-1">
                      I can't provide an adulthood {t.lowerSingular}
                    </h5>
                    <p className="text-xs text-gray-600 mb-3">
                      Tell us why you're unable to provide someone that knows you well in adulthood.
                    </p>
                    
                    <div className="space-y-2">
                      <textarea
                        value={adulthoodInformant.skipReason}
                        onChange={(e) => handleChange('adulthood', 'skipReason', e.target.value)}
                        onFocus={handleTextareaFocus}
                        onBlur={handleTextareaBlur}
                        placeholder="..."
                        aria-label={`Reason for not providing an adulthood ${t.lowerSingular}`}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-h-[80px] max-h-[200px] resize-none overflow-y-auto"
                        onInput={(e) => resizeTextarea(e.target)}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Back button to return to form */}
              <div className="flex justify-start">
                <button
                  type="button"
                  onClick={() => setShowAdulthoodSkipForm(false)}
                  className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                >
                  ← Go back to provide details instead
                </button>
            </div>
          </div>
        );
        }
        
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 mb-3">This is <b>someone who knows you well in adulthood</b>, for example, a partner or a close friend.</p>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="adulthood-firstName" className="block text-xs font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  id="adulthood-firstName"
                  type="text"
                  value={adulthoodInformant.firstName}
                  onChange={(e) => handleChange('adulthood', 'firstName', e.target.value)}
                  placeholder="..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>

              <div>
                <label htmlFor="adulthood-lastName" className="block text-xs font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  id="adulthood-lastName"
                  type="text"
                  value={adulthoodInformant.lastName}
                  onChange={(e) => handleChange('adulthood', 'lastName', e.target.value)}
                  placeholder="..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
            </div>

              <div>
                <label htmlFor="adulthood-email" className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="adulthood-email"
                  type="email"
                  value={adulthoodInformant.email}
                  onChange={(e) => handleChange('adulthood', 'email', e.target.value)}
                  placeholder="..."
                  aria-invalid={adulthoodInformant.email ? !validateEmail(adulthoodInformant.email) : undefined}
                  aria-describedby={
                    (adulthoodInformant.email && !validateEmail(adulthoodInformant.email)) ? 'adulthood-email-error' :
                    (adulthoodInformant.hasError && adulthoodInformant.errorMessage) ? 'adulthood-email-backend-error' :
                    undefined
                  }
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    adulthoodInformant.email && !validateEmail(adulthoodInformant.email)
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-violet-500 focus:border-violet-500'
                  }`}
                />
                {adulthoodInformant.email && !validateEmail(adulthoodInformant.email) && (
                  <p id="adulthood-email-error" className="mt-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-1 rounded-md" role="alert">
                    Please enter a valid email address
                  </p>
                )}
                {adulthoodInformant.hasError && adulthoodInformant.errorMessage && (
                  <p id="adulthood-email-backend-error" className="mt-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-1 rounded-md" role="alert">
                    {adulthoodInformant.errorMessage}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="adulthood-relationship" className="block text-xs font-medium text-gray-700 mb-1">
                  Relationship to You
                </label>
                <input
                  id="adulthood-relationship"
                  type="text"
                  value={adulthoodInformant.relationship}
                  onChange={(e) => handleChange('adulthood', 'relationship', e.target.value)}
                  placeholder="e.g. Partner, Close Friend"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
            </div>
            
            <button
              type="button"
              onClick={handleSkip}
              className="mt-2 text-xs text-violet-600 hover:text-violet-800 font-medium"
            >
              Unable to provide these details?
            </button>
          </div>
        );
      case 2:
        // Show success state if childhood informant was already successfully created
        if (childhoodInformant.isSuccess) {
          return (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4" role="status">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Successfully Added</h4>
                    <div className="space-y-1 text-sm text-green-800">
                      <p><span className="font-medium">Name:</span> {childhoodInformant.firstName} {childhoodInformant.lastName}</p>
                      <p className="break-all"><span className="font-medium">Email:</span> {childhoodInformant.email}</p>
                      <p><span className="font-medium">Relationship:</span> {childhoodInformant.relationship}</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Your childhood {t.lowerSingular} will complete questions about your childhood (before age 12).
              </p>
              <p className="text-sm text-gray-600">
                You'll be able to make changes later if needed.
              </p>
            </div>
          );
        }
        
        if (showChildhoodSkipForm) {
          const hasCopyOption = adulthoodInformant.isSuccess;
          
          if (hasCopyOption) {
            // Two-option design when Adulthood informant exists
            const adulthoodFirstName = adulthoodInformant.firstName 
              ? adulthoodInformant.firstName.charAt(0).toUpperCase() + adulthoodInformant.firstName.slice(1).toLowerCase()
              : `your ${t.lowerSingular}`;
            
            return (
              <div className="space-y-2">
                {/* Header with clear choice presentation */}
                <div className="text-left">
                  <h4 className="text-sm text-gray-600 mb-2">                    
                    If you're unable to provide a Childhood {t.singular}, choose one option below:
                  </h4>
                </div>
                
                {/* Option 1: Use existing informant (prioritized) */}
                <div className="bg-violet-50 border border-violet-200 rounded-md p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                      <DuplicateIcon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                        Use {adulthoodFirstName}'s details again
                      </h5>
                      <p className="text-xs text-gray-600 mb-3">
                        If {adulthoodFirstName} can answer questions about both your adulthood and childhood, you can click the button below:
                      </p>
                      <button
                        type="button"
                        onClick={handleReuseAdulthoodInformant}
                        className="w-full bg-violet-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-violet-700 transition-colors"
                      >
                        Use the same person for Childhood
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* "Or" divider */}
                <div className="flex items-center">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <div className="mx-3 text-xs text-gray-500 font-medium">or</div>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>
                
                {/* Option 2: Skip with reason */}
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                      <XIcon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                        Tell us why
                      </h5>
                      <p className="text-xs text-gray-600 mb-3">
                        Please tell us why you're not able to provide someone that knew you well in childhood (before age 12).
                      </p>
                      
                      <div className="space-y-2">
                        <textarea
                          value={childhoodInformant.skipReason}
                          onChange={(e) => handleChange('childhood', 'skipReason', e.target.value)}
                          onFocus={handleTextareaFocus}
                          onBlur={handleTextareaBlur}
                          placeholder="..."
                          aria-label={`Reason for not providing a childhood ${t.lowerSingular}`}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-h-[80px] max-h-[200px] resize-none overflow-y-auto"
                          onInput={(e) => resizeTextarea(e.target)}
                          rows={3}
                        />
                        <button
                          type="button"
                          onClick={handleNext}
                          disabled={!childhoodInformant.skipReason.trim()}
                          className={`w-full px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                            !childhoodInformant.skipReason.trim()
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-gray-600 text-white hover:bg-gray-700'
                          }`}
                        >
                          Submit Reason
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Back button to return to form */}
                <div className="flex justify-start">
                  <button
                    type="button"
                    onClick={() => setShowChildhoodSkipForm(false)}
                    className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                  >
                    ← Go back to provide details instead
                  </button>
                </div>
              </div>
            );
          } else {
            // Single-option design when no Adulthood informant exists
            return (
              <div className="space-y-4">
                {/* Clean single option card */}
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                      <XIcon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                        I can't provide a childhood {t.lowerSingular}
                      </h5>
                      <p className="text-xs text-gray-600 mb-3">
                        Tell us why you're unable to provide someone that knew you well in childhood (before age 12).
                      </p>
                      
                      <div className="space-y-2">
                        <textarea
                          value={childhoodInformant.skipReason}
                          onChange={(e) => handleChange('childhood', 'skipReason', e.target.value)}
                          onFocus={handleTextareaFocus}
                          onBlur={handleTextareaBlur}
                          placeholder="..."
                          aria-label={`Reason for not providing a childhood ${t.lowerSingular}`}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-h-[80px] max-h-[200px] resize-none overflow-y-auto"
                          onInput={(e) => resizeTextarea(e.target)}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back button to return to form */}
              <div className="flex justify-start">
                <button
                  type="button"
                  onClick={() => setShowChildhoodSkipForm(false)}
                  className="text-xs text-violet-600 hover:text-violet-800 font-medium"
                >
                  ← Go back to provide details instead
                </button>
              </div>
              </div>
            );
          }
        }
        
        return (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 mb-2">This is <b>someone who knew you well in childhood (before age 12)</b>, for example, a parent, a sibling, or a childhood friend.</p>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="childhood-firstName" className="block text-xs font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  id="childhood-firstName"
                  type="text"
                  value={childhoodInformant.firstName}
                  onChange={(e) => handleChange('childhood', 'firstName', e.target.value)}
                  placeholder="..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>

              <div>
                <label htmlFor="childhood-lastName" className="block text-xs font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  id="childhood-lastName"
                  type="text"
                  value={childhoodInformant.lastName}
                  onChange={(e) => handleChange('childhood', 'lastName', e.target.value)}
                  placeholder="..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
            </div>

              <div>
                <label htmlFor="childhood-email" className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="childhood-email"
                  type="email"
                  value={childhoodInformant.email}
                  onChange={(e) => handleChange('childhood', 'email', e.target.value)}
                  placeholder="..."
                  aria-invalid={childhoodInformant.email ? !validateEmail(childhoodInformant.email) : undefined}
                  aria-describedby={
                    (childhoodInformant.email && !validateEmail(childhoodInformant.email)) ? 'childhood-email-error' :
                    (childhoodInformant.hasError && childhoodInformant.errorMessage) ? 'childhood-email-backend-error' :
                    hasSameEmailWarning() ? 'childhood-email-same-warning' :
                    undefined
                  }
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    childhoodInformant.email && !validateEmail(childhoodInformant.email)
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : hasSameEmailWarning()
                        ? 'border-amber-400 focus:ring-amber-500 focus:border-amber-500'
                        : 'border-gray-300 focus:ring-violet-500 focus:border-violet-500'
                  }`}
                />
                {childhoodInformant.email && !validateEmail(childhoodInformant.email) && (
                  <p id="childhood-email-error" className="mt-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-1 rounded-md" role="alert">
                    Please enter a valid email address
                  </p>
                )}
                {childhoodInformant.hasError && childhoodInformant.errorMessage && (
                  <p id="childhood-email-backend-error" className="mt-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-1 rounded-md" role="alert">
                    {childhoodInformant.errorMessage}
                  </p>
                )}
                {hasSameEmailWarning() && !childhoodInformant.hasError && (
                  <p id="childhood-email-same-warning" className="mt-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-md" role="status">
                    This is the same email as your adulthood {t.lowerSingular}. If you'd like the same person to answer both questionnaires, select "Unable to provide these details?" below, then choose "Use the same person for Childhood".
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="childhood-relationship" className="block text-xs font-medium text-gray-700 mb-1">
                  Relationship to You
                </label>
                <input
                  id="childhood-relationship"
                  type="text"
                  value={childhoodInformant.relationship}
                  onChange={(e) => handleChange('childhood', 'relationship', e.target.value)}
                  placeholder="e.g. Parent, Sibling, Childhood Friend"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
            </div>
            
            <button
              type="button"
              onClick={handleSkip}
              className="mt-2 text-xs text-violet-600 hover:text-violet-800 font-medium"
            >
              Unable to provide these details?
            </button>
          </div>
        );
      case 3: // Reuse confirmation step
        if (reassignmentSuccess) {
          // Show success state
        return (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4" role="status">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Successfully Added</h4>
                    <div className="space-y-1 text-sm text-green-800">
                      <p><span className="font-medium">Name:</span> {adulthoodInformant.firstName} {adulthoodInformant.lastName}</p>
                      <p className="break-all"><span className="font-medium">Email:</span> {adulthoodInformant.email}</p>
                      <p><span className="font-medium">Relationship:</span> {adulthoodInformant.relationship}</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                This person will be asked to complete questions about your adulthood and childhood.
              </p>
              <p className="text-sm text-gray-600">
                You'll be able to make changes later if needed.
              </p>
          </div>
        );
        }
        
        // Show confirmation form
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              You've chosen to use your Adulthood {t.singular} for questions about your childhood as well.
            </p>
            
            {/* Show the informant details */}
            <div className="bg-violet-50 border border-violet-200 rounded-md p-4">
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="w-6 h-6 text-violet-600 flex-shrink-0" aria-hidden="true" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-violet-800 mb-2">{t.singular} Details</h4>
                  <div className="space-y-1 text-sm text-violet-800">
                    <p><span className="font-medium">Name:</span> {adulthoodInformant.firstName} {adulthoodInformant.lastName}</p>
                    <p className="break-all"><span className="font-medium">Email:</span> {adulthoodInformant.email}</p>
                    <p><span className="font-medium">Relationship:</span> {adulthoodInformant.relationship}</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600">
              This person will receive invitations for both your Adulthood and Childhood questionnaires.
            </p>
            
            {reassignError && (
              <WarningPanel variant="error">{reassignError}</WarningPanel>
            )}
            
          </div>
        );
      case 4: // Completion step
return (
      <div className="space-y-4">
            {/* Success header */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center" aria-hidden="true">
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>
              
              <p className="text-sm text-gray-700 mb-4">
                {(() => {
                  const hasAdulthood = !adulthoodInformant.isSkipped;
                  const hasChildhood = !childhoodInformant.isSkipped;
                  const informantCount = (hasAdulthood ? 1 : 0) + (hasChildhood ? 1 : 0);
                  
                  // Check if same informant is used for both (compare emails, case-insensitive and trimmed)
                  const isSameInformant = hasAdulthood && hasChildhood && 
                    adulthoodInformant.email.toLowerCase().trim() === childhoodInformant.email.toLowerCase().trim();
                  
                  if (informantCount === 2 && isSameInformant) {
                    return `Great! We've sent an invitation to your ${t.lowerSingular} to complete their questionnaires`;
                  } else if (informantCount === 2) {
                    return `Great! We've sent invitations to both of your ${t.lowerPlural} to complete their questionnaires.`;
                  } else if (informantCount === 1) {
                    return `We've sent an invitation to your ${t.lowerSingular} to complete their questionnaire.`;
                  } else {
                    return `Thank you for providing reasons why you couldn't provide details of any ${t.lowerPlural}.`;
                  }
                })()}
              </p>              
      </div>

            {/* Summary cards */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-900 text-left">Summary:</h4>
              
              {/* Adulthood Informant Summary */}
              <div className={`border rounded-md p-3 ${
                adulthoodInformant.isSkipped 
                  ? 'border-gray-200 bg-gray-50' 
                  : 'border-green-200 bg-green-50'
              }`}>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    {adulthoodInformant.isSkipped ? (
                      <XCircleIcon className="w-8 h-8 text-gray-500" />
                    ) : (
                      <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <h5 className="font-medium text-sm mb-1">Adulthood {t.singular}</h5>
                    {adulthoodInformant.isSkipped ? (
                      <div className="space-y-0.5">
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">No details provided</span>
                        </p>
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Reason given:</span> {adulthoodInformant.skipReason.substring(0, 20)}{adulthoodInformant.skipReason.length > 20 ? '...' : ''}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <p className="text-xs text-green-800">
                          <span className="font-medium">Name:</span> {adulthoodInformant.firstName} {adulthoodInformant.lastName}
                        </p>
                        <p className="text-xs text-green-800 truncate">
                          <span className="font-medium">Email:</span> {adulthoodInformant.email}
                        </p>
                        <p className="text-xs text-green-800 truncate">
                          <span className="font-medium">Relationship:</span> {adulthoodInformant.relationship}
                        </p>
                      </div>
                    )}
                  </div>
          </div>
        </div>
        
              {/* Childhood Informant Summary */}
              <div className={`border rounded-md p-3 ${
                childhoodInformant.isSkipped 
                  ? 'border-gray-200 bg-gray-50' 
                  : 'border-green-200 bg-green-50'
              }`}>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    {childhoodInformant.isSkipped ? (
                      <XCircleIcon className="w-8 h-8 text-gray-500" />
                    ) : (
                      <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <h5 className="font-medium text-sm mb-1">Childhood {t.singular}</h5>
                    {childhoodInformant.isSkipped ? (
                      <div className="space-y-0.5">
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">No details provided</span>
                        </p>
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Reason given:</span> {childhoodInformant.skipReason.substring(0, 20)}{childhoodInformant.skipReason.length > 20 ? '...' : ''}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <p className="text-xs text-green-800">
                          <span className="font-medium">Name:</span> {childhoodInformant.firstName} {childhoodInformant.lastName}
                        </p>
                        <p className="text-xs text-green-800 truncate">
                          <span className="font-medium">Email:</span> {childhoodInformant.email}
                        </p>
                        <p className="text-xs text-green-800 truncate">
                          <span className="font-medium">Relationship:</span> {childhoodInformant.relationship}
                        </p>
                      </div>
                    )}
                  </div>
          </div>
        </div>
            </div>
            
            <p className="text-center text-sm text-gray-700">
                Your questionnaires are now available!
              </p>

            <p className="text-center text-xs text-gray-500 mt-2">
              Need to make changes? You can find the '{t.plural}' button at the top of your home page to manage your {t.lowerPlural}.
            </p>

            {/* Footer with Finish button */}
            <div className="mt-4 flex justify-between">
              <div></div> {/* Empty div to maintain flex spacing */}
        <AlertDialog.Cancel asChild>
          <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors bg-violet-600 hover:bg-violet-700"
                >
                  <div className="flex items-center">
                    Finish
                    <ChevronRightIcon className="h-3 w-3 ml-1" aria-hidden="true" />
                  </div>
          </button>
        </AlertDialog.Cancel>
            </div>
  </div>
);
        
      default:
        return null;
    }
  };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => {
      // Don't allow closing during submission
      if (!open && !adulthoodInformant.isSubmitting && !childhoodInformant.isSubmitting) {
        onClose();
      }
    }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <AlertDialog.Content className={`radix-dialog-content-inset-motion fixed left-1/2 transform -translate-x-1/2 bg-white p-4 md:p-6 rounded-lg shadow-lg max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto z-50 transition-all duration-300 ease-in-out ${
          isKeyboardFocused 
            ? 'top-[20%] -translate-y-0 md:top-1/2 md:-translate-y-1/2' 
            : 'top-1/2 -translate-y-1/2'
        }`}>
          {/* Close button */}
          <button
            onClick={() => !adulthoodInformant.isSubmitting && !childhoodInformant.isSubmitting && onClose()}
            disabled={adulthoodInformant.isSubmitting || childhoodInformant.isSubmitting}
            className={`absolute top-4 right-4 p-1 rounded-full transition-colors ${
              adulthoodInformant.isSubmitting || childhoodInformant.isSubmitting
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            aria-label="Close dialog"
          >
            <XIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          
          <AlertDialog.Title className="text-lg md:text-xl font-semibold mb-3 md:mb-4 pr-8">
            {currentStep === 1 ? `Your Adulthood ${t.singular}` 
             : currentStep === 2 || currentStep === 3 ? `Your Childhood ${t.singular}`
             : currentStep === 4 ? `${t.singular} Set Up Complete`
             : `Adding Your ${t.plural}`}
          </AlertDialog.Title>
  
          <VisuallyHidden.Root>
            <AlertDialog.Description>
              {currentStep === 1 ? 'Form to add details for someone who knows you well in adulthood' 
               : currentStep === 2 || currentStep === 3 ? 'Form to add details for someone who knew you well in childhood'
               : currentStep === 4 ? `Summary of ${t.lowerSingular} setup completion`
               : `${t.lowerSingular} setup wizard`}
            </AlertDialog.Description>
          </VisuallyHidden.Root>
  
  
          <div className="my-3">
            {renderStepContent()}
          </div>
  
          {submitError && currentStep < 4 && (
            <div className="mt-2">
              <WarningPanel variant="error">{submitError}</WarningPanel>
            </div>
          )}
  
          {currentStep < 4 && (
            <div className="mt-4 flex justify-between">
              {currentStep > 1 && !(currentStep === 2 && showChildhoodSkipForm) && !(currentStep === 3 && reassignmentSuccess) && !(currentStep === 2 && childhoodInformant.isSuccess) ? (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={adulthoodInformant.isSubmitting || childhoodInformant.isSubmitting || isReassigning}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center">
                    <ChevronLeftIcon className="h-3 w-3 mr-1" aria-hidden="true" />
                    {(currentStep === 1 && showAdulthoodSkipForm) || currentStep === 3 ? 'Go back' : 'Back'}
                  </div>
                </button>
              ) : (
                <div></div> // Empty div to maintain flex spacing
              )}
  
              {/* Hide forward button when in two-option childhood skip form */}
              {!(currentStep === 2 && showChildhoodSkipForm && adulthoodInformant.isSuccess) && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!isStepValid() || adulthoodInformant.isSubmitting || childhoodInformant.isSubmitting || isReassigning}
                className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors
                  ${!isStepValid() || adulthoodInformant.isSubmitting || childhoodInformant.isSubmitting || isReassigning
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-violet-600 hover:bg-violet-700'}`}
              >
                {adulthoodInformant.isSubmitting || childhoodInformant.isSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <ButtonSpinner className="h-3 w-3" />
                    Setting up…
                  </span>
                ) : currentStep === 1 && !showAdulthoodSkipForm && adulthoodInformant.isSuccess ? (
                  <div className="flex items-center">
                    Continue
                    <ChevronRightIcon className="h-3 w-3 ml-1" aria-hidden="true" />
                  </div>
                ) : currentStep === 1 && !showAdulthoodSkipForm ? (
                  `Set up Adulthood ${t.singular}`
                ) : currentStep === 2 && !showChildhoodSkipForm && childhoodInformant.isSuccess ? (
                  <div className="flex items-center">
                    Continue
                    <ChevronRightIcon className="h-3 w-3 ml-1" aria-hidden="true" />
                  </div>
                ) : currentStep === 2 && !showChildhoodSkipForm ? (
                  `Set up Childhood ${t.singular}`
                ) : currentStep === 3 && reassignmentSuccess ? (
                  <div className="flex items-center">
                    Continue
                    <ChevronRightIcon className="h-3 w-3 ml-1" aria-hidden="true" />
                  </div>
                ) : currentStep === 3 ? (
                  isReassigning ? (
                    <span className="inline-flex items-center gap-2">
                      <ButtonSpinner className="h-3 w-3" />
                      Setting up…
                    </span>
                  ) : 'Proceed'
                ) : (
                  <div className="flex items-center">
                    Continue
                    <ChevronRightIcon className="h-3 w-3 ml-1" aria-hidden="true" />
                  </div>
                )}
              </button>
              )}
            </div>
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default InformantSetupForm;